
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const getCookiePreferences = (): CookiePreferences | null => {
  const preferences = localStorage.getItem('cookie-preferences');
  return preferences ? JSON.parse(preferences) : null;
};

export const hasConsentFor = (cookieType: keyof CookiePreferences): boolean => {
  const preferences = getCookiePreferences();
  return preferences ? preferences[cookieType] : false;
};

export const initializeAnalytics = (ga4MeasurementId: string) => {
  // Only initialize if user has consented to analytics cookies
  if (!hasConsentFor('analytics')) {
    return;
  }

  // Load Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ga4MeasurementId}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', ga4MeasurementId, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: 'secure;samesite=strict', // Enhanced security
  });
};

export const disableAnalytics = () => {
  // Disable Google Analytics if user revokes consent
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied'
    });
  }
};

// GDPR-compliant cookie management
export const setCookie = (name: string, value: string, days: number = 365) => {
  const preferences = getCookiePreferences();
  
  // Only set non-essential cookies if user has consented
  if (name !== 'cookie-consent' && name !== 'cookie-preferences') {
    if (!preferences?.necessary) {
      return;
    }
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;secure;samesite=strict`;
};

export const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  
  return null;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};
